import { Link, useStaticQuery, graphql } from 'gatsby';
import React, { FormEvent, useState, useEffect, FC, useCallback, useRef } from 'react';
import styled from 'styled-components';

import { CrowdfundingProductsQuery } from '../../../graphql-types';
import { useNPContext } from '../../npContext';
import Discount from '../../components/discount';
import SEO from '../../components/seo';
import { CrowdfundingProducts, ItemGroup } from '../../components/shared/cart/cartItems';
import {
    useLabeledProducts,
    LabeledProduct,
    getNormalProducts,
    getOrderedPreorderProducts,
} from '../../components/shared/cart/hooks';
import Layout from '../../components/shared/layout';
import Subtotal from '../../components/shared/subtotal';
import {
    StyledButton,
    StyledOption,
    StyledSelect,
    fontSize,
    weight,
    space,
} from '../../components/styles';
import constants from '../../constants';
import {
    ICentraCheckoutSelection,
    ICheckoutForm,
    IContext,
    ILayoutData,
    TPageTable,
} from '../../interfaces';
import { I18n, useDateFormatter, useI18n } from '../../i18n';
import translate from '../../translations';
import { CrowdfundingPaymentForm } from './crowdfundingPayment';
import { SimpleErrorFallback } from '../../components/SimpleErrorFallback';
import { ErrorBoundary } from 'react-error-boundary';
import { navigate } from 'gatsby';
import { createCentraAxiosApi } from '../../api/useCentraApi';
import { getMetadataFromPageTable } from '../../helpers/get-metadata';

const twoColumns = '(min-width: 1000px)';

interface Page {
    pageContext: {
        pageTable: TPageTable;
        layoutData: ILayoutData;
        failed?: boolean;
    };
}

const PaymentButton = styled(StyledButton)`
    width: 100%;
    margin-top: 50px;
    @media ${twoColumns} {
        width: calc(100% - 24px);
    }
`;

const CheckoutWrapper = styled.div`
    max-width: 1440px;
    margin: 24px auto ${space(10)};
    display: flex;
    flex-direction: column;
    @media ${twoColumns} {
        flex-direction: row;
    }
`;
const CheckoutItem = styled.div`
    width: calc(100% - 40px);
    margin: 0 auto;

    @media ${twoColumns} {
        width: calc(50% - 48px);
        margin: 24px;
    }
`;

const InputFieldDiv = styled.div`
    display: flex;
    flex-direction: column;
    margin: 10px 0;
    width: calc(100% - 24px);
`;
const Description = styled.div`
    color: grey;
    margin-top: 10px;
`;

const CheckoutTitle = styled.h2`
    margin: 0 0 24px;
`;
const InputField = styled.input`
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    width: calc(100% - 24px);
    height: 40px;
    border: 1px solid #ccc;
    border-radius: 4px;
    ${fontSize.regular};
    &[type='text'],
    &[type='tel'],
    &[type='email'] {
        padding: 0 12px;
    }
    ::placeholder,
    ::-webkit-input-placeholder {
        letter-spacing: 0.04em;
        color: #737373;
    }
    :-ms-input-placeholder {
        letter-spacing: -0.01em;
    }
    width: 100%;
    display: block;
`;
const ZipField = styled.div`
    width: calc(30% - 24px);
`;
const CityField = styled.div`
    width: calc(70% - 24px);
`;

const Label = styled.label`
    margin: 10px 0 0;
    display: block;
    & > input,
    & > select {
        margin: 10px 0 0;
    }
    & > .intl-tel-input {
        margin: 10px 0 0;
        display: block;
    }
`;

const CheckboxContainer = styled.div`
    display: inline-block;
    vertical-align: middle;
`;
// Checkbox
const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
    // Hide checkbox visually but remain accessible to screen readers.
    // Source: https://polished.js.org/docs/#hidevisually
    border: 0;
    clip: rect(0 0 0 0);
    clippath: inset(50%);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
`;
const Icon = styled.svg`
    fill: none;
    stroke: black;
    stroke-width: 3px;
`;
const StyledCheckbox = styled.div<{
    checked?: boolean;
}>`
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 1px solid ${constants.colors.pallet.black};
    background: white;
    border-radius: 3px;
    transition: all 150ms;
    ${HiddenCheckbox}:focus + & {
        box-shadow: 0 0 0 3px #d7ead5;
    }
    ${Icon} {
        visibility: ${props => (props.checked ? 'visible' : 'hidden')};
    }
`;

const ShippingAddressButton = styled.div`
    margin-top: 12px;
`;

const Checkboxes = styled.div`
    ${weight.medium};
    margin-top: 50px;
`;

const TermsLink = styled(Link)`
    color: ${constants.colors.pallet.black};
    text-decoration: underline;
`;

const initialState = {
    newsletter: false,
    termsAndConditions: false,
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    zipCode: '',
    city: '',
    state: '',
    country: '',
    phone: '',
    shippingFirstName: '',
    shippingLastName: '',
    shippingAddress: '',
    shippingZipCode: '',
    shippingCity: '',
    shippingState: '',
    shippingCountry: '',
};
export const Checkbox: React.FC<any> = ({ className, checked, ...props }) => (
    <CheckboxContainer className={className}>
        <HiddenCheckbox checked={checked} {...props} />
        <StyledCheckbox checked={checked}>
            <Icon viewBox="0 0 24 24">
                <polyline points="20 6 9 17 4 12" />
            </Icon>
        </StyledCheckbox>
    </CheckboxContainer>
);

const isValidUSZipCode = (zipCode: string | undefined): boolean =>
    /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(zipCode || '');

function useSignUpForm(country: string | undefined | null) {
    const [inputs, setInputs] = useState<ICheckoutForm>({
        ...initialState,
        country: country || '',
        shippingCountry: country || '',
    });

    // this is not pretty
    const handleInputChange = (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | { phone: string }
            | { newsletter: boolean }
            | { termsAndConditions: boolean }
    ) => {
        if ('phone' in event) {
            setInputs(inputs => ({ ...inputs, phone: event.phone }));
            return;
        }
        if ('newsletter' in event) {
            setInputs(inputs => ({ ...inputs, newsletter: event.newsletter }));
            return;
        }
        if ('termsAndConditions' in event) {
            setInputs(inputs => ({
                ...inputs,
                termsAndConditions: event.termsAndConditions,
            }));
            return;
        }
        event.persist();

        // Clear state field for shipping or billing address when the
        // respective country field is different than US.
        const resetStateField: { state?: string; shippingState?: string } = {};
        if (event.target.name === 'country' && event.target.value.toLowerCase() !== 'us') {
            resetStateField.state = '';
        } else if (
            event.target.name === 'shippingCountry' &&
            event.target.value.toLowerCase() !== 'us'
        ) {
            resetStateField.shippingState = '';
        }

        setInputs(inputs => ({
            ...inputs,
            ...resetStateField,
            [event.target.name]: event.target.value,
        }));
    };

    return {
        handleInputChange,
        inputs,
    };
}

type CentraCheckoutPaymentCallbackData = {
    detail: {
        responseEventRequired: boolean;
        paymentMethod: string;
        shippingAddress: unknown;
        billingAddress: unknown;
        paymentMethodSpecificFields: unknown;
    };
};

const PaymentForm: React.FC<{
    selection: ICentraCheckoutSelection | undefined;
    language: string;
    formHtml: string;
}> = ({ selection, language, formHtml }) => {
    const [error, setError] = useState<Error | null>(null);

    let context = useNPContext();
    let contextRef = useRef<IContext>(context);
    contextRef.current = context;
    const apiToken = context.centraApiToken;

    const handleCheckoutPaymentCallbackAsync = useCallback(
        async (origdata: CentraCheckoutPaymentCallbackData) => {
            const postData = origdata.detail;
            const responseEventRequired = postData.responseEventRequired;

            const centraApi = createCentraAxiosApi(apiToken);

            const baseUrl =
                typeof window !== `undefined`
                    ? document.location.hostname === 'localhost'
                        ? 'http://localhost:8000'
                        : document.location.origin
                    : process.env.GATSBY_STOREFRONT_URL || 'https://northernplayground.no';

            const response = await centraApi.post('/payment', {
                termsAndConditions: true,
                paymentReturnPage: `${baseUrl}/api/${language}/payment-redirect`,
                paymentMethod: postData.paymentMethod,
                shippingAddress: postData.shippingAddress,
                address: postData.billingAddress,
                paymentMethodSpecificFields: postData.paymentMethodSpecificFields,
            });
            const data = response.data as {
                action: string;
                errors?: Record<string, string>;
                messages?: string[];
                url?: string;
                token: string;
                formFields: unknown;
            };

            if (responseEventRequired) {
                if (data.action === 'redirect') {
                    location.href = data.url!;
                    return;
                }
                if (data.errors) {
                    // Payment failed for some reason, show error
                    throw new Error(`${data.errors} ${data.messages}`);
                }

                if (data.action === 'success') {
                    // Provider check for path and don't refresh selection when url is check-payment
                    // we don't want to do that until _after_ we have got the payment result
                    await contextRef.current.centra.refreshSelection();
                    await navigate(`/${language}/success`);
                    return;
                }

                if (data.action === 'javascript') {
                    const updateEvent = new CustomEvent('centra_checkout_payment_response', {
                        detail: data.formFields,
                    });
                    document.dispatchEvent(updateEvent);
                    return;
                }

                console.error('Unknown centra event', data);
                throw new Error('Got unknown event from Centra');
            }
        },
        [apiToken, language]
    );

    const handleCheckoutPaymentCallback = useCallback(
        (origdata: unknown) => {
            handleCheckoutPaymentCallbackAsync(origdata as CentraCheckoutPaymentCallbackData).catch(
                err => {
                    setError(err as Error);
                    console.error(err);
                }
            );
        },
        [handleCheckoutPaymentCallbackAsync]
    );

    useEffect(() => {
        document.addEventListener(
            'centra_checkout_payment_callback',
            handleCheckoutPaymentCallback
        );

        return () => {
            document.removeEventListener(
                'centra_checkout_payment_callback',
                handleCheckoutPaymentCallback
            );
        };
    }, [handleCheckoutPaymentCallback]);

    useEffect(() => {
        function getItemName(item: ICentraCheckoutSelection['items'][0]) {
            return `${translate(language, item.product.gender_selection_gender)} ${
                item.size
            } ${translate(language, item.product.color_swatch.code)} ${item.product.name}`;
        }

        try {
            // Klaviyo docs: https://developers.klaviyo.com/en/v1-2/docs/guide-to-integrating-a-platform-without-a-pre-built-klaviyo-integration#started-checkout
            const klaviyoStartedCheckoutData = {
                $event_id: `started-checkout-${Math.floor(Date.now() / 1000)}`,
                $value: selection?.totals.grandTotalPriceAsNumber,
                $currency: selection?.currency,
                ItemNames: selection?.items.map(item => getItemName(item)),
                CheckoutUrl: `${window.location.origin}/${language}/checkout`,
                Categories: selection?.items.map(item => item.product.collectionName),
                Items: selection?.items.map(item => ({
                    ProductID: item.product.centraProduct,
                    SKU: item.product.sku,
                    ProductName: getItemName(item),
                    Quantity: item.quantity,
                    ItemPrice: item.priceEachAsNumber,
                    RowTotal: item.priceEachAsNumber ?? 0 * item.quantity ?? 1,
                    // "ProductURL": item.product.legacyUrlEnUnisex,
                    ImageURL: item.product?.media?.standard?.[0],
                    ProductCategories: item.product.collectionName,
                })),
            };

            const klaviyoIdentifyData = {
                $email: selection?.address?.email,
                $first_name: selection?.address?.firstName,
                $last_name: selection?.address?.lastName,
            };

            console.log('> identify data', klaviyoIdentifyData);
            console.log('> started checkout data', klaviyoStartedCheckoutData);

            window._learnq?.push(['identify', klaviyoIdentifyData]);
            window._learnq?.push(['track', 'Started Checkout', klaviyoStartedCheckoutData]);
        } catch (error) {
            console.error('Error sending Klaviyo event', error);
        }

        window.dataLayer &&
            window.dataLayer.push({
                event: 'checkout',
                ecomm_prodid: selection?.items.map(item => item.product.centraProduct),
                ecomm_pagetype: 'cart',
                ecomm_totalvalue: selection?.totals.grandTotalPriceAsNumber,
                ecommerce: {
                    currencyCode: selection?.currency,
                    checkout: {
                        actionField: { step: 2, option: 'payment information' },
                        products: selection?.items.map(item => ({
                            name: item.product.name,
                            id: item.product.centraProduct,
                            price: item.priceEachAsNumber,
                            brand: 'Northern Playground',
                            category: item.product.collectionName,
                            variant: `${translate(
                                language,
                                item.product.gender_selection_gender
                            )} ${item.size} ${translate(language, 'in')} ${
                                item.product.color_swatch.name
                            }`,
                            quantity: item.quantity,
                        })),
                    },
                },
            });
    }, []);

    const checkoutContainerRef = (checkoutContainer: HTMLDivElement) => {
        evaluate(checkoutContainer);
    };

    return (
        <div>
            {error && (
                <div
                    css={`
                        background: ${constants.colors.pallet.error};
                        padding: ${space(2)} ${space(3)};
                        border-radius: 5px;
                        color: white;
                    `}
                >
                    Payment failed: {error.toString()}
                </div>
            )}
            <div
                ref={checkoutContainerRef}
                style={{ padding: '20px' }}
                dangerouslySetInnerHTML={{ __html: formHtml }}
            ></div>
        </div>
    );
};

type Payment =
    | {
          type: 'not_ready';
      }
    | {
          type: 'centra';
          formHtml: string;
          inputs: ICheckoutForm;
          formType: string;
      }
    | {
          type: 'crowdfunding';
          inputs: ICheckoutForm;
          separateShippingAddress: boolean;
      };

const CheckoutPage: React.FC<Page> = ({ pageContext: { pageTable, layoutData, failed } }) => {
    const [errors, setErrors] = useState<{ code: number; data: unknown } | null>(null);
    const [payment, setPayment] = useState<Payment>({ type: 'not_ready' });
    const context = useNPContext();

    const { language } = layoutData;

    const metadata = getMetadataFromPageTable({
        pageTable: pageTable,
        language: language,
        withDefaultTag: true,
    });

    return (
        <Layout
            pageTable={pageTable}
            layoutData={layoutData}
            // Disable cart when on the payment-part
            disableCart={payment.type !== 'not_ready'}
            disableFooter={true}
            disableAbandonmentPopup={true}
        >
            <SEO
                title="Checkout"
                lang={layoutData.language}
                noindex={true}
                canonicalURL={metadata?.canonicalUrl}
                alternates={metadata?.alternates}
            />
            <CheckoutWrapper>
                <Errors errors={errors} />
                <CheckoutItem style={{ height: 'fit-content' }}>
                    <ErrorBoundary FallbackComponent={SimpleErrorFallback}>
                        <CheckoutTitle>
                            <I18n>checkout</I18n>
                        </CheckoutTitle>
                        {failed && (
                            <div
                                css={`
                                    background: ${constants.colors.pallet.error};
                                    padding: ${space(2)} ${space(3)};
                                    border-radius: 5px;
                                    color: white;
                                `}
                            >
                                Payment failed
                            </div>
                        )}
                        {payment.type === 'centra' ? (
                            <PaymentForm
                                selection={context?.state?.centra?.selection}
                                language={layoutData.language}
                                formHtml={payment.formHtml}
                            />
                        ) : payment.type === 'crowdfunding' ? (
                            <CrowdfundingPaymentForm
                                inputs={payment.inputs}
                                separateShippingAddress={payment.separateShippingAddress}
                            />
                        ) : (
                            <CheckoutForm
                                setPayment={setPayment}
                                context={context}
                                setErrorFn={setErrors}
                                language={layoutData.language}
                            />
                        )}
                    </ErrorBoundary>
                </CheckoutItem>
                <CheckoutItem>
                    <CheckoutCart paymentShowing={payment.type !== 'not_ready'} />
                </CheckoutItem>
            </CheckoutWrapper>
        </Layout>
    );
};

const CheckoutCart: FC<{ paymentShowing: boolean }> = ({ paymentShowing }) => {
    let context = useNPContext();
    const selection: ICentraCheckoutSelection | undefined = context?.state?.centra?.selection;
    const labeledProducts = useLabeledProducts(selection?.items);

    return (
        <div
            css={`
                display: ${paymentShowing ? 'none' : 'block'};
                @media ${twoColumns} {
                    display: block;
                }
            `}
        >
            <h2
                css={`
                    margin: 0 0 24px;
                `}
            >
                &nbsp;
            </h2>
            <Packages labeledProducts={labeledProducts} currency={selection?.currency} />
            <CrowdfundingProducts
                labeledProducts={labeledProducts}
                currency={selection?.currency || ''}
                alternate
            />
            <Subtotal
                selection={context?.state?.centra?.selection}
                labeledProducts={labeledProducts}
            />
        </div>
    );
};

const Packages: React.FC<{ labeledProducts: LabeledProduct[]; currency?: string }> = ({
    labeledProducts,
    currency,
}) => {
    const normalProducts = getNormalProducts(labeledProducts);
    const preorderProducts = getOrderedPreorderProducts(labeledProducts);
    const dateCategories =
        normalProducts.length === 0
            ? preorderProducts
            : [{ date: null, products: normalProducts }, ...preorderProducts];
    const dateFormatter = useDateFormatter();

    return (
        <>
            {dateCategories.map((group, index) => {
                const marginTop = index === 0 ? '0' : '40px';
                return (
                    <div
                        key={index}
                        css={`
                            margin-bottom: 24px,
                            margin-top: ${marginTop},
                        `}
                    >
                        <div
                            css={`
                                ${fontSize.regular}
                                ${weight.medium};
                            `}
                        >
                            <I18n>package</I18n> {index + 1}/{dateCategories.length}
                        </div>
                        <div
                            css={`
                                ${fontSize.regular};
                                color: #737373;
                            `}
                        >
                            {group.date ? (
                                <>
                                    <I18n>expectedDelivery</I18n>: {dateFormatter(group.date)}
                                </>
                            ) : (
                                <I18n>immediateDelivery</I18n>
                            )}
                        </div>
                        <ItemGroup items={group.products} currency={currency ?? 'NOK'} alternate />
                    </div>
                );
            })}
        </>
    );
};

const Errors: React.FC<{ errors: { code: number; data: any } | null }> = ({ errors }) => {
    if (!errors) {
        return null;
    }
    console.log('errors are:', errors);
    switch (errors.code) {
        case 406: {
            // Invalid fields
            return null;
        }
        case 410: {
            // If any products are unavailable
            const unavailable = errors.data.unavailable;
            console.log('unavailable:', unavailable);
            return <p>{"it looks like we don't have enough of that product!"}</p>;
        }
        case 412: {
            return <p>{'no items in selection'}</p>;
        }
        default: {
            return <p>{'unknown error!'}</p>;
        }
    }
};

const evaluate = (subtree: HTMLDivElement) => {
    let context = window;

    function getSnippetType(script: HTMLScriptElement) {
        return script.type || '';
    }
    function getRawSnippet(script: HTMLScriptElement) {
        return script.text || script.textContent || script.innerHTML || '';
    }

    if (subtree) {
        try {
            const scripts = subtree.getElementsByTagName('script');
            const scriptsAsArray = Array.from(scripts).filter(
                script => getSnippetType(script) !== 'application/json'
            );

            scriptsAsArray.forEach(script => {
                const unescaped = getRawSnippet(script).replace(/\\/gi, '');
                eval.call(context, unescaped);
            });
        } catch (error) {
            // tslint:disable-next-line:no-console
            console.error('Catched error in evaluate function:', error);
        }
    }
};

const findStatesFromCountry = (context: IContext, country: string) => {
    const countryObject =
        context?.state?.centra?.countries &&
        country &&
        context.state.centra.countries.find(c => c.country === country);

    return (countryObject && countryObject.states) || null;
};

export const CheckoutForm: React.FC<{
    setPayment: (value: Payment) => void;
    context: IContext;
    setErrorFn: (error: { code: number; data: unknown }) => void;
    language: string;
}> = ({ context, setErrorFn, language, setPayment }) => {
    const { t } = useI18n();
    let crowdfundingProducts = useStaticQuery<CrowdfundingProductsQuery>(graphql`
        query CrowdfundingProducts {
            allProduct(filter: { productState: { eq: "crowdFunding" } }) {
                nodes {
                    centraId
                }
            }
        }
    `);

    const { inputs, handleInputChange } = useSignUpForm(context.state.centra?.location?.country);
    const [submitAttempted, setSubmitAttempted] = React.useState(false);

    const [separateShippingAddress, setSeparateShippingAddress] = useState<boolean>(false);
    const initialCountry = context.state.centra?.location.country;

    const zipCodeError =
        inputs.country === 'US' &&
        !isValidUSZipCode(inputs.zipCode) &&
        'Invalid zip code. Accepted US zip code formats are 12345 or 12345-1234';
    const shippingZipCodeError =
        separateShippingAddress &&
        inputs.shippingCountry === 'US' &&
        !isValidUSZipCode(inputs.shippingZipCode) &&
        'Invalid zip code. Accepted US zip code formats are 12345 or 12345-1234';
    const emailError = (!inputs.email || inputs.email.length < 4) && t('emailRequired');
    const phoneError = (!inputs.phone || inputs.phone.length < 4) && t('phoneRequired');

    const hasErrors = zipCodeError || shippingZipCodeError || emailError || phoneError;

    const selection: ICentraCheckoutSelection | undefined = context?.state?.centra?.selection;

    useEffect(() => {
        window.dataLayer &&
            window.dataLayer.push({
                event: 'checkout',
                ecomm_prodid: selection?.items.map(item => item.product.centraProduct),
                ecomm_pagetype: 'cart',
                ecomm_totalvalue: selection?.totals.grandTotalPriceAsNumber,
                ecommerce: {
                    currencyCode: selection?.currency,
                    checkout: {
                        actionField: { step: 1, option: 'user information' },
                        products: selection?.items.map(item => ({
                            name: item.product.name,
                            id: item.product.centraProduct,
                            price: item.priceEachAsNumber,
                            brand: 'Northern Playground',
                            category: item.product.collectionName,
                            variant: `${translate(
                                language,
                                item.product.gender_selection_gender
                            )} ${item.size} ${translate(language, 'in')} ${
                                item.product.color_swatch.name
                            }`,
                            quantity: item.quantity,
                        })),
                    },
                },
            });
    }, []);

    const initialStates =
        (initialCountry && findStatesFromCountry(context, initialCountry)) || null;

    const [countryStates, setCountryStates] = useState<{ state: string; name: string }[] | null>(
        initialStates
    );
    const [shippingCountryStates, setShippingCountryStates] = useState<
        { state: string; name: string }[] | null
    >(initialStates);
    useEffect(() => {
        setCountryStates(
            (inputs.country && findStatesFromCountry(context, inputs.country)) || null
        );

        setShippingCountryStates(
            (inputs.shippingCountry && findStatesFromCountry(context, inputs.shippingCountry)) ||
                null
        );
    }, [inputs]);

    const changeCountry = (event: React.ChangeEvent<HTMLSelectElement>) => {
        handleInputChange(event);
        context.userPreferences.setCountry(event.target.value);
    };

    const successFn = async (json: any) => {
        if (json.formType === 'adyen-checkout' || json.formType === 'adyen-drop-in') {
            setPayment({
                type: 'centra',
                formHtml: json.formHtml,
                inputs: inputs,
                formType: json.formType,
            });
        } else if (json.action === 'success') {
            // If the customer has used a coupon with a value greater than or equal to the checkout sum
            // centra sends a direct success json message.
            // https://docs.centra.com/fe-development/payments/payment-flows#direct-success
            context.centra
                .refreshSelection()
                .then(async () => {
                    await navigate(`/${language}/success`);
                })
                .catch(err => console.error(err));
        }
    };

    const errorFn = (code: number, json: any) => {
        console.log('got errors when clicking in checkout:', code, json);
        setErrorFn({ code: code, data: json });
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        if (event) {
            event.preventDefault();
        }

        setSubmitAttempted(true);

        if (hasErrors) {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            return false;
        }
        if (inputs.newsletter) {
            window.dataLayer &&
                window.dataLayer.push({
                    event: 'newsletter',
                    eventCategory: 'newsletter',
                    eventAction: 'signup',
                    eventLabel: 'checkout page',
                });
        }

        window.scrollTo(0, 0);

        let crowdfundingProductIds = new Set(
            crowdfundingProducts.allProduct.nodes.map(product => product.centraId)
        );

        let hasACrowdfundingProduct = selection?.items.some(item =>
            crowdfundingProductIds.has(item.product.centraProduct)
        );

        // Use one of the two payment-flows, decide which depending on if the user has added
        // a crowdfunding-product to the cart
        if (hasACrowdfundingProduct) {
            setPayment({ type: 'crowdfunding', inputs, separateShippingAddress });
        } else {
            context.centra.initiatePayment(
                language,
                inputs,
                separateShippingAddress,
                successFn,
                errorFn
            );
        }
    };

    return (
        <React.Fragment>
            {(zipCodeError || shippingZipCodeError) && submitAttempted && (
                <p style={{ color: 'red', marginTop: '0' }}>
                    Invalid zip code. Accepted US zip code formats are 12345 or 12345-1234
                </p>
            )}
            <form onSubmit={event => handleSubmit(event)}>
                <InputFieldDiv>
                    <Label>
                        {translate(language, 'email')}
                        <InputField
                            type="email"
                            name="email"
                            placeholder={translate(language, 'enterEmail')}
                            onChange={handleInputChange}
                            value={inputs.email}
                            required
                        ></InputField>
                    </Label>
                </InputFieldDiv>
                {emailError && submitAttempted && (
                    <p style={{ color: 'red', marginTop: '0' }}>{emailError}</p>
                )}
                <InputFieldDiv>
                    <Label>
                        {translate(language, 'phonenumber')}
                        <InputField
                            placeholder="00 00 00 00"
                            type="tel"
                            name="phone"
                            autoComplete="tel-national"
                            required
                            onChange={handleInputChange}
                        />
                    </Label>
                    <Description>{translate(language, 'weNeedThisInformation')}</Description>
                </InputFieldDiv>
                {phoneError && submitAttempted && (
                    <p style={{ color: 'red', marginTop: '0' }}>{phoneError}</p>
                )}
                <InputFieldDiv>
                    <Label>
                        {translate(language, 'firstname')}
                        <InputField
                            type="text"
                            name="firstName"
                            placeholder={translate(language, 'enterFirstname')}
                            onChange={handleInputChange}
                            //value={inputs.firstName}
                            required
                        ></InputField>
                    </Label>
                </InputFieldDiv>
                <InputFieldDiv>
                    <Label>
                        {translate(language, 'lastname')}
                        <InputField
                            type="text"
                            name="lastName"
                            placeholder={translate(language, 'enterLastname')}
                            onChange={handleInputChange}
                            //value={inputs.lastName}
                            required
                        ></InputField>
                    </Label>
                </InputFieldDiv>
                <InputFieldDiv>
                    <Label>
                        {translate(language, 'adress')}
                        <InputField
                            type="text"
                            name="address"
                            placeholder={translate(language, 'adressDescription')}
                            onChange={handleInputChange}
                            //value={inputs.address}
                            required
                        ></InputField>
                    </Label>
                </InputFieldDiv>
                <div>
                    <InputFieldDiv
                        style={{ flexDirection: 'row', justifyContent: 'space-between' }}
                    >
                        <ZipField>
                            <Label>
                                {translate(language, 'zipcode')}
                                <InputField
                                    type="text"
                                    name="zipCode"
                                    placeholder="1234"
                                    onChange={handleInputChange}
                                    //value={inputs.zipCode}
                                    required
                                ></InputField>
                            </Label>
                        </ZipField>
                        <CityField>
                            <Label>
                                {translate(language, 'city')}
                                <InputField
                                    type="text"
                                    name="city"
                                    placeholder={translate(language, 'city')}
                                    onChange={handleInputChange}
                                    //value={inputs.city}
                                    required
                                ></InputField>
                            </Label>
                        </CityField>
                    </InputFieldDiv>
                    {zipCodeError && submitAttempted && (
                        <p style={{ color: 'red', marginTop: '0' }}>
                            Invalid zip code. Accepted US zip code formats are 12345 or 12345-1234
                        </p>
                    )}
                </div>
                <Label>
                    {translate(language, 'country')}
                    <StyledSelect
                        id="cars"
                        name="country"
                        form="carform"
                        onChange={event =>
                            separateShippingAddress
                                ? handleInputChange(event)
                                : changeCountry(event)
                        }
                        defaultValue={initialCountry}
                    >
                        {context?.state?.centra?.countries
                            ? context.state.centra.countries.map(country => (
                                  <StyledOption value={country.country} key={country.country}>
                                      {country.name}
                                  </StyledOption>
                              ))
                            : null}
                    </StyledSelect>
                    {!separateShippingAddress &&
                        inputs.country !== 'NO' &&
                        '* Taxes and customs may be added in your country'}
                </Label>
                {countryStates && (
                    <Label>
                        {translate(language, 'state')}
                        <StyledSelect
                            id="cars"
                            name="state"
                            form="carform"
                            onChange={event => handleInputChange(event)}
                            defaultValue={initialCountry}
                        >
                            <StyledOption value={''} key={'unselected'}>
                                {translate(language, 'chooseState')}
                            </StyledOption>
                            {countryStates.map(state => (
                                <StyledOption value={state.state} key={state.state}>
                                    {state.name}
                                </StyledOption>
                            ))}
                        </StyledSelect>
                    </Label>
                )}
                <ShippingAddressButton>
                    <UppercaseLabel style={{ color: constants.colors.pallet.black }}>
                        <Checkbox
                            checked={separateShippingAddress}
                            onChange={() => {
                                setSeparateShippingAddress(!separateShippingAddress);
                            }}
                        />
                        <span style={{ marginLeft: '10px' }}>
                            {translate(language, 'addShippingAddress')}
                        </span>
                    </UppercaseLabel>
                </ShippingAddressButton>
                {separateShippingAddress && (
                    <React.Fragment>
                        <InputFieldDiv>
                            <Label>
                                {translate(language, 'firstname')}
                                <InputField
                                    type="text"
                                    name="shippingFirstName"
                                    placeholder={translate(language, 'enterFirstname')}
                                    onChange={handleInputChange}
                                    required
                                ></InputField>
                            </Label>
                        </InputFieldDiv>
                        <InputFieldDiv>
                            <Label>
                                {translate(language, 'lastname')}
                                <InputField
                                    type="text"
                                    name="shippingLastName"
                                    placeholder={translate(language, 'enterLastname')}
                                    onChange={handleInputChange}
                                    required
                                ></InputField>
                            </Label>
                        </InputFieldDiv>
                        <InputFieldDiv>
                            <Label>
                                {translate(language, 'adress')}
                                <InputField
                                    type="text"
                                    name="shippingAddress"
                                    placeholder={translate(language, 'adressDescription')}
                                    onChange={handleInputChange}
                                    required
                                ></InputField>
                            </Label>
                        </InputFieldDiv>
                        <div>
                            <InputFieldDiv
                                style={{
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <ZipField>
                                    <Label>
                                        {translate(language, 'zipcode')}
                                        <InputField
                                            type="text"
                                            name="shippingZipCode"
                                            placeholder="1234"
                                            onChange={handleInputChange}
                                            required
                                        ></InputField>
                                    </Label>
                                </ZipField>
                                <CityField>
                                    <Label>
                                        {translate(language, 'city')}
                                        <InputField
                                            type="text"
                                            name="shippingCity"
                                            placeholder={translate(language, 'city')}
                                            onChange={handleInputChange}
                                            required
                                        ></InputField>
                                    </Label>
                                </CityField>
                            </InputFieldDiv>
                            {shippingZipCodeError && submitAttempted && (
                                <p style={{ color: 'red', marginTop: '0' }}>
                                    Invalid zip code. Accepted US zip code formats are 12345 or
                                    12345-1234
                                </p>
                            )}
                        </div>

                        <Label>
                            {translate(language, 'country')}
                            <StyledSelect
                                id="cars"
                                name="shippingCountry"
                                form="carform"
                                onChange={changeCountry}
                                defaultValue={initialCountry}
                            >
                                {context?.state?.centra?.countries
                                    ? context.state.centra.countries.map(country => (
                                          <StyledOption
                                              value={country.country}
                                              key={country.country}
                                          >
                                              {country.name}
                                          </StyledOption>
                                      ))
                                    : null}
                            </StyledSelect>
                            {inputs.shippingCountry !== 'NO' &&
                                '* Taxes and customs may be added in your country'}
                        </Label>
                        {shippingCountryStates && (
                            <React.Fragment>
                                <Label>
                                    {translate(language, 'state')}
                                    <StyledSelect
                                        id="cars"
                                        name="shippingState"
                                        form="carform"
                                        onChange={event => handleInputChange(event)}
                                        defaultValue={
                                            (context.state.centra &&
                                                context.state.centra.location.state) ||
                                            undefined
                                        }
                                    >
                                        <StyledOption value={''} key={'unselected'}>
                                            {translate(language, 'chooseState')}
                                        </StyledOption>
                                        {shippingCountryStates.map(state => (
                                            <StyledOption value={state.state} key={state.state}>
                                                {state.name}
                                            </StyledOption>
                                        ))}
                                    </StyledSelect>
                                </Label>
                            </React.Fragment>
                        )}
                    </React.Fragment>
                )}
                <Discount context={context} selection={selection} language={language} />

                <Checkboxes>
                    <div
                        css={`
                            margin-bottom: ${space(1)};
                        `}
                    >
                        <UppercaseLabel style={{ color: '#737373' }}>
                            <Checkbox
                                checked={inputs.newsletter}
                                onChange={(event: any) => {
                                    handleInputChange({ newsletter: event.target.checked });
                                }}
                            />
                            <span style={{ marginLeft: '10px' }}>
                                {translate(language, 'signUpConfirmation')}
                            </span>
                        </UppercaseLabel>
                    </div>
                    <div>
                        <UppercaseLabel style={{ color: constants.colors.pallet.black }}>
                            <Checkbox
                                checked={inputs.termsAndConditions}
                                onChange={(event: any) => {
                                    handleInputChange({
                                        termsAndConditions: event.target.checked,
                                    });
                                }}
                            />
                            <span style={{ marginLeft: '10px' }}>
                                {translate(language, 'iAgree')}{' '}
                                <TermsLink to={translate(language, 'termsUrl')}>
                                    {translate(language, 'termsAndConditions')}
                                </TermsLink>
                            </span>
                        </UppercaseLabel>
                    </div>
                </Checkboxes>
                <PaymentButton type="submit" disabled={!inputs.termsAndConditions}>
                    {inputs.termsAndConditions
                        ? translate(language, 'continueToPayment')
                        : translate(language, 'agreeTermsFirst')}
                </PaymentButton>
            </form>
        </React.Fragment>
    );
};

const UppercaseLabel = styled.label`
    letter-spacing: 0.07em;
    ${fontSize.small};
    ${weight.medium};
`;

export default CheckoutPage;
